// NotificationsPanel.jsx — dropdown de notificacións con polling cada 60s
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck } from "lucide-react";
import { notificationService } from "../../api/services";
import { useAuth } from "../../context/AuthContext";
import { formatDate } from "../../utils/formatters";
import Spinner from "./Spinner";

// Deriva a ruta de navegación dun tipo de notificación
function getNotificationLink(n) {
  const data = n.data ?? {};
  switch (n.type) {
    case "NEW_RESERVATION":
      return "/propietario?tab=reservations";
    case "NEW_FINCA_RESERVATION":
      // Nova solicitude de arrendamento de finca → propietario debe
      // aprobar/rexeitar dende o tab Reservas do panel propietario.
      return "/propietario?tab=reservations";
    case "RETURN_CONFIRMED":
    case "PAYMENT_RECEIVED":
      return "/propietario";
    case "RESERVATION_CONFIRMED":
    case "CONTRACT_PENDING":
    case "RESERVATION_CANCELLED":
    case "PENDING_REVIEW_REMINDER":
    case "REVIEW_RECEIVED":
      return "/reservas";
    case "INCIDENT_OPENED":
    case "INCIDENT_RESOLVED":
      return data.reservationId ? "/reservas" : "/propietario";
    case "LEASE_CONTRACT_PENDING":
    case "LEASE_CONFIRMED":
      // O contrato afecta ao arrendatario (asinar) e ao propietario
      // (estado da reserva). Por defecto ao tab reservas do propietario.
      return "/propietario?tab=reservations";
    case "NEW_MESSAGE":
      return data.conversationId ? `/mensaxes` : "/mensaxes";
    default:
      return null;
  }
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora mesmo";
  if (min < 60) return `fai ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `fai ${h}h`;
  return formatDate(dateStr, { month: "short", day: "numeric" });
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    if (!user) return;
    try {
      const data = await notificationService.list();
      const list = Array.isArray(data) ? data : data?.notifications ?? [];
      setNotifications(list);
    } catch {
      // silently ignore - not critical
    }
  };

  useEffect(() => {
    if (!user) { setNotifications([]); return; }
    fetch();
    const interval = setInterval(fetch, 15000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const unreadCount = notifications.filter(n => !n.read && !n.readAt).length;

  return { notifications, unreadCount, refresh: fetch };
}

export default function NotificationsPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const panelRef = useRef(null);
  const { notifications, unreadCount, refresh } = useNotifications();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!user) return null;

  const handleMarkAll = async () => {
    setMarking(true);
    try {
      await notificationService.markAllRead();
      await refresh();
    } catch { /* ignore */ }
    finally { setMarking(false); }
  };

  const handleMarkOne = async (n) => {
    try {
      await notificationService.markRead(n.id);
      await refresh();
    } catch { /* ignore */ }
    const link = getNotificationLink(n);
    if (link) {
      setOpen(false);
      navigate(link);
    }
  };

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-full hover:bg-subtle transition-colors"
        aria-label="Notificacións"
      >
        <Bell size={20} className="text-dark" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 leading-none">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-border rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-dark text-sm">Notificacións</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  disabled={marking}
                  className="text-xs text-muted hover:text-dark flex items-center gap-1 transition-colors"
                >
                  {marking ? <Spinner size={12} /> : <><CheckCheck size={12} /> Marcar todas lidas</>}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-subtle rounded-full">
                <X size={14} className="text-muted" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-10 text-muted text-sm">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                Sen notificacións
              </div>
            ) : (
              notifications.slice(0, 30).map(n => {
                const isUnread = !n.read && !n.readAt;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleMarkOne(n)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-subtle transition-colors ${isUnread ? "bg-blue-50/40" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {isUnread && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                      <div className={isUnread ? "" : "ml-4"}>
                        <p className={`text-sm ${isUnread ? "font-semibold text-dark" : "text-dark"}`}>
                          {n.title || n.message || "Nova notificación"}
                        </p>
                        {n.body && n.body !== n.title && (
                          <p className="text-xs text-muted mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-xs text-muted mt-1">{timeAgo(n.createdAt)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
